/**
 * Hero & Header settings with a live desktop/mobile preview.
 *
 * Only controlled options are exposed (no free-form CSS). The preview renders the
 * real SiteHeader + HeroSection components, so what admins see is what ships.
 */

import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import {
  AlertTriangle, Check, Image as ImageIcon, Layout, Monitor, RotateCcw, Save, Smartphone, Upload,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import apiClient from '@/utils/api';
import SiteHeader from '@/components/SiteHeader';
import HeroSection from '@/components/HeroSection';
import {
  HEADER_DEFAULTS, HERO_DEFAULTS, OVERLAY_PRESETS, directionForAlignment,
} from '@/utils/heroStyles';
import { checkButtonContrast, checkHeroContrast } from '@/utils/contrast';

const LIMITS = { headline: 120, subheadline: 300, label: 30, image_alt: 150 };

const ColorField = ({ id, label, value, onChange, testId }) => (
  <div className="space-y-2">
    <Label htmlFor={id}>{label}</Label>
    <div className="flex items-center gap-2">
      <input
        id={id}
        type="color"
        value={value}
        onChange={(e) => onChange(e.target.value.toUpperCase())}
        className="h-10 w-14 cursor-pointer rounded border border-border bg-transparent"
        data-testid={testId}
      />
      <Input value={value} onChange={(e) => onChange(e.target.value.toUpperCase())} className="font-mono" />
    </div>
  </div>
);

const SliderField = ({ label, value, min, max, step = 1, suffix = '', onChange, testId }) => (
  <div className="space-y-2">
    <div className="flex items-center justify-between">
      <Label>{label}</Label>
      <span className="text-sm text-muted-foreground" data-testid={`${testId}-value`}>
        {value}{suffix}
      </span>
    </div>
    <Slider value={[value]} min={min} max={max} step={step} onValueChange={(v) => onChange(v[0])} data-testid={testId} />
  </div>
);

const CharCount = ({ value, limit }) => (
  <p className={`text-xs ${String(value || '').length > limit ? 'text-destructive' : 'text-muted-foreground'}`}>
    {String(value || '').length}/{limit}
  </p>
);

export const HeroHeaderSettings = ({ branding }) => {
  const [hero, setHero] = useState(HERO_DEFAULTS);
  const [header, setHeader] = useState(HEADER_DEFAULTS);
  const [device, setDevice] = useState('desktop');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    load();
  }, []);

  const load = async () => {
    try {
      const [heroRes, statsRes] = await Promise.all([
        apiClient.get('/settings/hero'),
        apiClient.get('/settings/cta-clicks/stats?days=30').catch(() => null),
      ]);
      setHero({ ...HERO_DEFAULTS, ...heroRes.data.hero });
      setHeader({ ...HEADER_DEFAULTS, ...heroRes.data.header });
      if (statsRes) setStats(statsRes.data);
    } catch (e) {
      toast.error('Failed to load hero settings');
    } finally {
      setLoading(false);
    }
  };

  const setHeroField = (field, value) => setHero((h) => ({ ...h, [field]: value }));
  const setCta = (which, field, value) =>
    setHero((h) => ({ ...h, [which]: { ...h[which], [field]: value } }));

  const applyAlignment = (alignment) =>
    setHero((h) => ({ ...h, content_alignment: alignment, gradient_direction: directionForAlignment(alignment) }));

  const applyPreset = (preset) => {
    setHero((h) => ({ ...h, ...preset.values }));
    toast.success(`"${preset.name}" preset applied — remember to save`);
  };

  const applyTextMode = (mode) => {
    if (mode === 'light') {
      setHero((h) => ({ ...h, text_mode: mode, heading_color: '#FFFFFF', body_color: '#E5E7EB' }));
    } else if (mode === 'dark') {
      setHero((h) => ({ ...h, text_mode: mode, heading_color: '#0F172A', body_color: '#334155' }));
    } else {
      setHeroField('text_mode', mode);
    }
  };

  const contrast = useMemo(() => checkHeroContrast(hero), [hero]);
  const ctaContrast = useMemo(() => checkButtonContrast(hero.cta_bg_color, hero.cta_text_color), [hero.cta_bg_color, hero.cta_text_color]);
  const gsContrast = useMemo(
    () => checkButtonContrast(header.get_started_bg_color, header.get_started_text_color),
    [header.get_started_bg_color, header.get_started_text_color]
  );

  const validate = () => {
    const errors = [];
    if (!hero.headline.trim()) errors.push('Headline is required');
    if (hero.headline.length > LIMITS.headline) errors.push(`Headline must be ${LIMITS.headline} characters or fewer`);
    if (hero.subheadline.length > LIMITS.subheadline) errors.push(`Subheadline must be ${LIMITS.subheadline} characters or fewer`);
    if (!hero.image_alt.trim()) errors.push('Image alt text is required');
    const links = [
      ['Primary CTA link', hero.primary_cta.url],
      ['Secondary CTA link', hero.secondary_cta.url],
      ['Get Started link', header.get_started_url],
      ['Login link', header.login_url],
      ['Hero image', hero.image_url],
    ];
    links.forEach(([name, url]) => {
      if (!url || !(url.startsWith('/') || url.startsWith('http://') || url.startsWith('https://'))) {
        errors.push(`${name} must be an internal path (/apply) or a full http(s) URL`);
      }
    });
    [['Primary CTA label', hero.primary_cta.label], ['Secondary CTA label', hero.secondary_cta.label],
      ['Get Started label', header.get_started_label], ['Login label', header.login_label]]
      .forEach(([name, label]) => {
        if (!label?.trim()) errors.push(`${name} is required`);
        else if (label.length > LIMITS.label) errors.push(`${name} must be ${LIMITS.label} characters or fewer`);
      });
    return errors;
  };

  const save = async () => {
    const errors = validate();
    if (errors.length) {
      toast.error(errors[0]);
      return;
    }
    setSaving(true);
    try {
      await apiClient.put('/settings/hero', hero);
      await apiClient.put('/settings/header', header);
      toast.success('Hero and header published');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save hero settings');
    } finally {
      setSaving(false);
    }
  };

  const resetToDefaults = async () => {
    setSaving(true);
    try {
      const res = await apiClient.post('/settings/hero/reset');
      setHero({ ...HERO_DEFAULTS, ...res.data.hero });
      setHeader({ ...HEADER_DEFAULTS, ...res.data.header });
      toast.success('Reset to defaults');
    } catch (e) {
      toast.error('Failed to reset');
    } finally {
      setSaving(false);
    }
  };

  const uploadImage = async (file) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Hero image must be a JPG, PNG or WebP file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Hero image must be 5 MB or smaller');
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      form.append('type', 'hero_image');
      const res = await apiClient.post('/settings/upload-image', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setHero((h) => ({ ...h, image_url: res.data.relative_url, image_url_full: res.data.url }));
      toast.success('Hero image uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">Loading hero settings…</CardContent>
      </Card>
    );
  }

  const isMobilePreview = device === 'mobile';

  return (
    <Card data-testid="hero-settings-card">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Layout className="h-5 w-5" />
          Hero &amp; Header
        </CardTitle>
        <CardDescription>
          Full-bleed hero image, overlay and the public header actions. Changes appear in the live preview
          immediately and go live when you publish.
        </CardDescription>
      </CardHeader>

      <CardContent className="space-y-8">
        {/* Live preview */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-base">Live preview</Label>
            <Tabs value={device} onValueChange={setDevice}>
              <TabsList>
                <TabsTrigger value="desktop" data-testid="preview-desktop-tab">
                  <Monitor className="mr-2 h-4 w-4" /> Desktop
                </TabsTrigger>
                <TabsTrigger value="mobile" data-testid="preview-mobile-tab">
                  <Smartphone className="mr-2 h-4 w-4" /> Mobile
                </TabsTrigger>
              </TabsList>
            </Tabs>
          </div>

          <div className="flex justify-center rounded-xl border border-border bg-muted/30 p-4">
            <div
              className="overflow-hidden rounded-lg border border-border shadow-lg"
              style={{ width: isMobilePreview ? 390 : '100%', maxWidth: isMobilePreview ? 390 : 980 }}
              data-testid="hero-live-preview"
            >
              <div className="relative" style={{ height: isMobilePreview ? 520 : 420 }}>
                <div className="absolute inset-x-0 top-0 z-20">
                  <SiteHeader branding={branding} header={header} overHero mobile={isMobilePreview} preview />
                </div>
                <HeroSection hero={hero} mobile={isMobilePreview} preview />
              </div>
            </div>
          </div>

          {!contrast.passes && (
            <div
              className="flex items-start gap-3 rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900"
              data-testid="hero-contrast-warning"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
              <div>
                <p className="font-medium">Contrast may fail WCAG AA</p>
                <p>
                  Heading {contrast.headingRatio}:1 (needs 3:1){!contrast.headingPasses && ' ✗'} · Body{' '}
                  {contrast.bodyRatio}:1 (needs 4.5:1){!contrast.bodyPasses && ' ✗'}. Increase the overlay opacity
                  or pick a stronger text colour.
                </p>
              </div>
            </div>
          )}
          {contrast.passes && (
            <p className="flex items-center gap-2 text-sm text-emerald-700" data-testid="hero-contrast-ok">
              <Check className="h-4 w-4" /> Contrast passes WCAG AA (heading {contrast.headingRatio}:1, body{' '}
              {contrast.bodyRatio}:1)
            </p>
          )}
        </div>

        {/* Content */}
        <section className="space-y-4">
          <h4 className="font-medium">Hero content</h4>
          <div className="space-y-2">
            <Label htmlFor="heroHeadline">Headline</Label>
            <Input
              id="heroHeadline"
              value={hero.headline}
              maxLength={LIMITS.headline + 20}
              onChange={(e) => setHeroField('headline', e.target.value)}
              data-testid="hero-headline-input"
            />
            <CharCount value={hero.headline} limit={LIMITS.headline} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="heroSubheadline">Subheadline</Label>
            <Textarea
              id="heroSubheadline"
              rows={3}
              value={hero.subheadline}
              onChange={(e) => setHeroField('subheadline', e.target.value)}
              data-testid="hero-subheadline-input"
            />
            <CharCount value={hero.subheadline} limit={LIMITS.subheadline} />
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3 rounded-lg border border-border p-4">
              <p className="text-sm font-medium">Primary CTA</p>
              <Input
                value={hero.primary_cta.label}
                onChange={(e) => setCta('primary_cta', 'label', e.target.value)}
                placeholder="Label"
                data-testid="hero-primary-label"
              />
              <Input
                value={hero.primary_cta.url}
                onChange={(e) => setCta('primary_cta', 'url', e.target.value)}
                placeholder="/apply"
                data-testid="hero-primary-url"
              />
              <div className="flex items-center justify-between">
                <Label htmlFor="heroPrimaryNewTab" className="text-sm">Open in new tab</Label>
                <Switch
                  id="heroPrimaryNewTab"
                  checked={hero.primary_cta.new_tab}
                  onCheckedChange={(v) => setCta('primary_cta', 'new_tab', v)}
                  data-testid="hero-primary-newtab"
                />
              </div>
            </div>

            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Secondary CTA</p>
                <Switch
                  checked={hero.secondary_cta.show}
                  onCheckedChange={(v) => setCta('secondary_cta', 'show', v)}
                  data-testid="hero-secondary-show"
                />
              </div>
              <Input
                value={hero.secondary_cta.label}
                onChange={(e) => setCta('secondary_cta', 'label', e.target.value)}
                placeholder="Label"
                data-testid="hero-secondary-label"
              />
              <Input
                value={hero.secondary_cta.url}
                onChange={(e) => setCta('secondary_cta', 'url', e.target.value)}
                placeholder="/login"
                data-testid="hero-secondary-url"
              />
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-3">
            <ColorField id="heroCtaBg" label="CTA background" value={hero.cta_bg_color}
              onChange={(v) => setHeroField('cta_bg_color', v)} testId="hero-cta-bg" />
            <ColorField id="heroCtaText" label="CTA text" value={hero.cta_text_color}
              onChange={(v) => setHeroField('cta_text_color', v)} testId="hero-cta-text" />
            <SliderField label="CTA corner radius" value={hero.cta_radius} min={0} max={40} suffix="px"
              onChange={(v) => setHeroField('cta_radius', v)} testId="hero-cta-radius" />
          </div>
          {!ctaContrast.passes && (
            <p className="text-sm text-amber-700" data-testid="hero-cta-contrast-warning">
              CTA text contrast is {ctaContrast.ratio}:1 — below the 4.5:1 AA minimum.
            </p>
          )}
        </section>

        {/* Image */}
        <section className="space-y-4">
          <h4 className="font-medium">Hero image</h4>
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild variant="outline" disabled={uploading}>
              <label className="cursor-pointer">
                <Upload className="mr-2 h-4 w-4" />
                {uploading ? 'Uploading…' : 'Upload image'}
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  className="hidden"
                  onChange={(e) => uploadImage(e.target.files?.[0])}
                  data-testid="hero-image-upload"
                />
              </label>
            </Button>
            <span className="text-xs text-muted-foreground">JPG, PNG or WebP · max 5 MB · resized automatically</span>
          </div>
          <div className="space-y-2">
            <Label htmlFor="heroImageUrl" className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" /> Image URL
            </Label>
            <Input
              id="heroImageUrl"
              value={hero.image_url}
              onChange={(e) => setHero((h) => ({ ...h, image_url: e.target.value, image_url_full: undefined }))}
              data-testid="hero-image-url"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="heroImageAlt">Image alt text (required)</Label>
            <Input
              id="heroImageAlt"
              value={hero.image_alt}
              onChange={(e) => setHeroField('image_alt', e.target.value)}
              data-testid="hero-image-alt"
            />
            <CharCount value={hero.image_alt} limit={LIMITS.image_alt} />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <SliderField label="Focal point — horizontal" value={hero.focal_x} min={0} max={100} suffix="%"
              onChange={(v) => setHeroField('focal_x', v)} testId="hero-focal-x" />
            <SliderField label="Focal point — vertical" value={hero.focal_y} min={0} max={100} suffix="%"
              onChange={(v) => setHeroField('focal_y', v)} testId="hero-focal-y" />
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Hero height</Label>
              <Select value={hero.height} onValueChange={(v) => setHeroField('height', v)}>
                <SelectTrigger data-testid="hero-height-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="compact">Compact (55% of screen)</SelectItem>
                  <SelectItem value="standard">Standard (72%)</SelectItem>
                  <SelectItem value="tall">Tall (90%)</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {hero.height === 'custom' && (
              <SliderField label="Custom height" value={hero.custom_height_vh} min={40} max={100} suffix="vh"
                onChange={(v) => setHeroField('custom_height_vh', v)} testId="hero-custom-height" />
            )}
          </div>
        </section>

        {/* Overlay */}
        <section className="space-y-4">
          <h4 className="font-medium">Overlay &amp; readability</h4>
          <div className="flex flex-wrap gap-2">
            {OVERLAY_PRESETS.map((preset) => (
              <Button key={preset.id} variant="outline" size="sm" onClick={() => applyPreset(preset)}
                data-testid={`hero-preset-${preset.id}`}>
                {preset.name}
              </Button>
            ))}
          </div>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Content alignment</Label>
              <Select value={hero.content_alignment} onValueChange={applyAlignment}>
                <SelectTrigger data-testid="hero-alignment-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="left">Left</SelectItem>
                  <SelectItem value="center">Center</SelectItem>
                  <SelectItem value="right">Right</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">The gradient follows the content side automatically.</p>
            </div>
            <div className="space-y-2">
              <Label>Gradient direction</Label>
              <Select value={hero.gradient_direction} onValueChange={(v) => setHeroField('gradient_direction', v)}>
                <SelectTrigger data-testid="hero-direction-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="to_right">Left → right</SelectItem>
                  <SelectItem value="to_left">Right → left</SelectItem>
                  <SelectItem value="to_top">Bottom → top</SelectItem>
                  <SelectItem value="to_bottom">Top → bottom</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">On phones the gradient always switches to bottom → top.</p>
            </div>
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <ColorField id="heroOverlayColor" label="Overlay colour" value={hero.overlay_color}
              onChange={(v) => setHeroField('overlay_color', v)} testId="hero-overlay-color" />
            <SliderField label="Overlay opacity (content side)" value={hero.overlay_opacity} min={0} max={100}
              suffix="%" onChange={(v) => setHeroField('overlay_opacity', v)} testId="hero-overlay-opacity" />
            <SliderField label="Gradient coverage" value={hero.gradient_coverage} min={30} max={100} suffix="%"
              onChange={(v) => setHeroField('gradient_coverage', v)} testId="hero-gradient-coverage" />
          </div>
          <div className="grid gap-4 md:grid-cols-3">
            <div className="space-y-2">
              <Label>Text colour mode</Label>
              <Select value={hero.text_mode} onValueChange={applyTextMode}>
                <SelectTrigger data-testid="hero-textmode-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="light">Light text</SelectItem>
                  <SelectItem value="dark">Dark text</SelectItem>
                  <SelectItem value="custom">Custom</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <ColorField id="heroHeadingColor" label="Heading colour" value={hero.heading_color}
              onChange={(v) => setHero((h) => ({ ...h, heading_color: v, text_mode: 'custom' }))}
              testId="hero-heading-color" />
            <ColorField id="heroBodyColor" label="Body colour" value={hero.body_color}
              onChange={(v) => setHero((h) => ({ ...h, body_color: v, text_mode: 'custom' }))}
              testId="hero-body-color" />
          </div>
        </section>

        {/* Header */}
        <section className="space-y-4">
          <h4 className="font-medium">Header actions</h4>
          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Get Started button</p>
                <Switch
                  checked={header.get_started_show}
                  onCheckedChange={(v) => setHeader((h) => ({ ...h, get_started_show: v }))}
                  data-testid="header-get-started-show"
                />
              </div>
              <Input
                value={header.get_started_label}
                onChange={(e) => setHeader((h) => ({ ...h, get_started_label: e.target.value }))}
                placeholder="Get Started"
                data-testid="header-get-started-label"
              />
              <Input
                value={header.get_started_url}
                onChange={(e) => setHeader((h) => ({ ...h, get_started_url: e.target.value }))}
                placeholder="/apply"
                data-testid="header-get-started-url"
              />
              <div className="flex items-center justify-between">
                <Label className="text-sm">Open in new tab</Label>
                <Switch
                  checked={header.get_started_new_tab}
                  onCheckedChange={(v) => setHeader((h) => ({ ...h, get_started_new_tab: v }))}
                  data-testid="header-get-started-newtab"
                />
              </div>
              <div className="grid gap-3 sm:grid-cols-2">
                <ColorField id="gsBg" label="Background" value={header.get_started_bg_color}
                  onChange={(v) => setHeader((h) => ({ ...h, get_started_bg_color: v }))} testId="header-gs-bg" />
                <ColorField id="gsText" label="Text" value={header.get_started_text_color}
                  onChange={(v) => setHeader((h) => ({ ...h, get_started_text_color: v }))} testId="header-gs-text" />
              </div>
              <SliderField label="Corner radius" value={header.get_started_radius} min={0} max={999} suffix="px"
                onChange={(v) => setHeader((h) => ({ ...h, get_started_radius: v }))} testId="header-gs-radius" />
              {!gsContrast.passes && (
                <p className="text-sm text-amber-700" data-testid="header-gs-contrast-warning">
                  Get Started text contrast is {gsContrast.ratio}:1 — below the 4.5:1 AA minimum.
                </p>
              )}
            </div>

            <div className="space-y-3 rounded-lg border border-border p-4">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Login button</p>
                <Switch
                  checked={header.login_show}
                  onCheckedChange={(v) => setHeader((h) => ({ ...h, login_show: v }))}
                  data-testid="header-login-show"
                />
              </div>
              <Input
                value={header.login_label}
                onChange={(e) => setHeader((h) => ({ ...h, login_label: e.target.value }))}
                placeholder="Login"
                data-testid="header-login-label"
              />
              <Input
                value={header.login_url}
                onChange={(e) => setHeader((h) => ({ ...h, login_url: e.target.value }))}
                placeholder="/login"
                data-testid="header-login-url"
              />
              <div className="space-y-2 pt-2">
                <Label>Header style over the hero</Label>
                <Select value={header.style_over_hero} onValueChange={(v) => setHeader((h) => ({ ...h, style_over_hero: v }))}>
                  <SelectTrigger data-testid="header-style-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="transparent">Always transparent</SelectItem>
                    <SelectItem value="solid_on_scroll">Transparent, solid on scroll</SelectItem>
                    <SelectItem value="solid">Always solid</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        </section>

        {/* Click analytics */}
        {stats && (
          <section className="space-y-2" data-testid="cta-click-stats">
            <h4 className="font-medium">Which entry point drives applications (last {stats.days} days)</h4>
            <div className="grid gap-3 sm:grid-cols-3">
              {[
                ['Header Get Started', stats.counts.header_get_started],
                ['Hero primary CTA', stats.counts.hero_primary],
                ['Hero secondary CTA', stats.counts.hero_secondary],
              ].map(([label, count]) => (
                <div key={label} className="rounded-lg border border-border p-4">
                  <p className="text-sm text-muted-foreground">{label}</p>
                  <p className="text-2xl font-bold">{count}</p>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="flex flex-col gap-3 border-t border-border pt-4 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={resetToDefaults} disabled={saving} data-testid="hero-reset-btn">
            <RotateCcw className="mr-2 h-4 w-4" /> Reset to default
          </Button>
          <Button onClick={save} disabled={saving} data-testid="hero-save-btn">
            <Save className="mr-2 h-4 w-4" /> {saving ? 'Publishing…' : 'Publish changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

export default HeroHeaderSettings;
